import { useUser } from '@folklore/auth';
import classNames from 'classnames';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { useContest } from '../../contexts/ContestContext';
import { useThemeClassName, useTheme } from '../../contexts/ThemeContext';
import FlameIcon from '../icons/Flame';
import Stats from './Stats';

// import * as AppPropTypes from '../../lib/PropTypes';
import styles from '../../../styles/partials/participations-stats.module.scss';

interface ParticipationsStatsProps {
    display?: 'normal' | 'table';
    className?: string | null;
    statsClassName?: string | null;
    valueClassName?: string | null;
}

function ParticipationsStats({
    display = 'normal',
    className = null,
    statsClassName = null,
    valueClassName = null,
}: ParticipationsStatsProps) {
    const { stats: userStats, draws } = useUser() as {
        stats: {
            remainingParticipationsToday?: number | null;
            drawParticipationsThisWeek?: number | null;
            participationsThisWeek?: number | null;
            maxParticipationsToday: number;
        };
        draws: { title: string; participationsCount: number }[];
    };
    const { name: theme } = useTheme();
    const themeClassName = useThemeClassName(styles);
    const { started = false } = useContest();
    const {
        remainingParticipationsToday = null,
        drawParticipationsThisWeek = null,
        participationsThisWeek = null,
        maxParticipationsToday,
    } = userStats || {};

    const isTable = display === 'table';

    const stats = useMemo(
        () =>
            [
                ...(draws || []).map(({ title, participationsCount }) => ({
                    value: participationsCount,
                    label: (
                        <FormattedMessage
                            defaultMessage="{count, plural, =0 {Participations} one {Participation} other {Participations}} to the {draw}"
                            description="ParticipationStats draw participations label"
                            values={{
                                count: participationsCount,
                                draw: title,
                            }}
                        />
                    ),
                })),
                remainingParticipationsToday !== null && maxParticipationsToday > 1 && started
                    ? {
                          value: isTable
                              ? `${remainingParticipationsToday}/${maxParticipationsToday}`
                              : remainingParticipationsToday,
                          label: (
                              <FormattedMessage
                                  defaultMessage="Remaining tries for today"
                                  description="ParticipationsStats label"
                              />
                          ),
                      }
                    : null,
                isTable && drawParticipationsThisWeek !== null && started
                    ? {
                          value: `${participationsThisWeek}/${drawParticipationsThisWeek}`,
                          label: (
                              <div>
                                  <div className={styles.labelContent}>
                                      <FormattedMessage
                                          defaultMessage="Play {count} times this week"
                                          description="ParticipationsStats weekly participations label"
                                          values={{ count: drawParticipationsThisWeek }}
                                      />
                                  </div>
                                  <div className={styles.note}>
                                      <FormattedMessage
                                          defaultMessage="For more chances to win the Grand Prize."
                                          description="Note"
                                      />
                                  </div>
                              </div>
                          ),
                      }
                    : null,
            ].filter((it) => it !== null),
        [
            isTable,
            remainingParticipationsToday,
            drawParticipationsThisWeek,
            participationsThisWeek,
            maxParticipationsToday,
            draws,
            theme,
        ],
    );

    if (stats === null || stats.length === 0) {
        return null;
    }

    return (
        <div
            className={classNames([
                styles.container,
                themeClassName,
                className,
                {
                    [styles[`${display}Display`]]: display !== null,
                },
            ])}
        >
            <Stats
                stats={stats}
                className={classNames([
                    styles.stats,
                    {
                        [statsClassName]: statsClassName !== null,
                    },
                ])}
                display={display}
                valueClassName={classNames([
                    styles.label,
                    {
                        [valueClassName]: valueClassName !== null,
                    },
                ])}
            />
            {!isTable && drawParticipationsThisWeek !== null && started ? (
                <div className={classNames([styles.section, styles.flames])}>
                    <div className={styles.icons}>
                        {[...Array(drawParticipationsThisWeek)].map((_, index) => (
                            <FlameIcon
                                key={`icon-${index + 1}`}
                                color={participationsThisWeek > index ? 'red' : 'currentColor'}
                                className={styles.icon}
                            />
                        ))}
                    </div>
                    <div className={styles.label}>
                        <FormattedMessage
                            defaultMessage="Play {count} times this week to get additionnal participations"
                            description="ParticipationsStats weekly play more label"
                            values={{ count: drawParticipationsThisWeek }}
                        />
                    </div>
                </div>
            ) : null}
        </div>
    );
}

export default ParticipationsStats;
